import { z } from "zod";
import siteDefault from "./site.default.json";

/** A pricing plan. The default config ships zero plans; add entries to
 *  `pricing.plans` (bundled or runtime JSON) and pricing UI appears everywhere. */
export interface PricingPlan {
  id: string;
  name: string;
  price: number | string;
  unit?: string;
  from?: boolean;
  description?: string;
  features?: string[];
  featured?: boolean;
  cta?: { label: string; to: string };
}

export interface SocialLink {
  label: string;
  href: string;
}

type Defaults = typeof siteDefault;

export type SiteConfig = Omit<Defaults, "pricing" | "footer"> & {
  pricing: Omit<Defaults["pricing"], "plans"> & { plans: PricingPlan[] };
  footer: Omit<Defaults["footer"], "social"> & { social: SocialLink[] };
};

export const defaultConfig = siteDefault as SiteConfig;

/** Runtime overrides are validated loosely: every top-level key must be an
 *  object/array if present. Unknown keys are ignored, malformed files are
 *  discarded in favour of the bundled defaults. */
export const overrideSchema = z
  .object({
    brand: z.record(z.unknown()).optional(),
    assets: z.record(z.unknown()).optional(),
    theme: z.record(z.unknown()).optional(),
    featureFlags: z.record(z.unknown()).optional(),
    nav: z.record(z.unknown()).optional(),
    home: z.record(z.unknown()).optional(),
    services: z.record(z.unknown()).optional(),
    pricing: z.record(z.unknown()).optional(),
    about: z.record(z.unknown()).optional(),
    contact: z.record(z.unknown()).optional(),
    faq: z.record(z.unknown()).optional(),
    footer: z.record(z.unknown()).optional(),
    seo: z.record(z.unknown()).optional(),
    integrations: z.record(z.unknown()).optional(),
  })
  .passthrough();

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Deep merge: objects merge key-by-key, arrays and primitives replace. */
export function deepMerge<T>(base: T, override: unknown): T {
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return override === undefined ? base : (override as T);
  }
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(override)) {
    if (value === undefined) continue;
    out[key] = isPlainObject(out[key]) ? deepMerge(out[key], value) : value;
  }
  return out as T;
}

export function mergeConfig(override: unknown): SiteConfig {
  const parsed = overrideSchema.safeParse(override);
  if (!parsed.success) {
    console.warn("[config] runtime override failed validation, using defaults");
    return defaultConfig;
  }
  return deepMerge(defaultConfig, parsed.data);
}

/** Prefix a config-relative asset path with the configured CDN/S3 base URL. */
export function assetUrl(config: SiteConfig, path: string | undefined): string {
  if (!path) return "";
  if (/^(https?:)?\/\//.test(path) || path.startsWith("data:")) return path;
  const base = config.assets.baseUrl?.replace(/\/$/, "") ?? "";
  if (!base) return path;
  return `${base}/${path.replace(/^\//, "")}`;
}
